import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Typography,
  LinearProgress,
} from "@mui/material";
import { io } from "socket.io-client";

const socket = io(process.env.REACT_APP_BACKEND_URL, {
  transports: ["websocket"],
});

export default function SendVerificationEmails() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null); // { total, processed, sent, failed, currentEmail }
  const jobIdRef = useRef(null);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const adminUserId = user?._id;

  useEffect(() => {
    if (!adminUserId) return;

    socket.emit("join-admin", adminUserId);

    const handleProgress = (data) => {
      if (jobIdRef.current && data.jobId !== jobIdRef.current) return;
      jobIdRef.current = data.jobId;
      setProgress(data);
    };

    const handleComplete = (data) => {
      if (jobIdRef.current && data.jobId !== jobIdRef.current) return;
      setProgress((prev) => ({ ...prev, ...data, processed: data.total }));
    };

    const handleError = (data) => {
      if (jobIdRef.current && data.jobId !== jobIdRef.current) return;
      setError(data.message || "Failed to send emails");
      setLoading(false);
    };

    socket.on("verification-email-progress", handleProgress);
    socket.on("verification-email-complete", handleComplete);
    socket.on("verification-email-error", handleError);

    return () => {
      socket.off("verification-email-progress", handleProgress);
      socket.off("verification-email-complete", handleComplete);
      socket.off("verification-email-error", handleError);
    };
  }, [adminUserId]);

  const handleSendEmails = async () => {
    if (
      !window.confirm(
        "This will send verification emails to all unverified users. Continue?",
      )
    ) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProgress(null);
    jobIdRef.current = null;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/send-verification-emails`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send emails");
      }

      if (data.total) {
        setProgress({
          jobId: data.jobId,
          total: data.total,
          processed: data.total,
          sent: data.sent,
          failed: data.failed,
        });
      }

      setSuccess(
        `Emails sent successfully. Sent: ${data.sent ?? 0}, Failed: ${data.failed ?? 0}`,
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const total = progress?.total || 0;
  const processed = progress?.processed || 0;
  const percent = total ? Math.round((processed / total) * 100) : 0;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSendEmails}
        disabled={loading}
        sx={{ minWidth: 220, backgroundColor: "var(--primary-color)" }}
      >
        {loading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          "Send Email Verification"
        )}
      </Button>

      {(loading || (progress && total > 0)) && (
        <Box sx={{ mt: 2, maxWidth: 400 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {processed} out of {total} emails processed
            {progress?.currentEmail && processed < total
              ? ` (sending to ${progress.currentEmail}...)`
              : ""}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 8,
              borderRadius: 4,
              "& .MuiLinearProgress-bar": {
                backgroundColor: "var(--primary-color)",
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Sent: {progress?.sent || 0} · Failed: {progress?.failed || 0}
          </Typography>
        </Box>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
